import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import AddEditAdNetworkModal from '../components/adnetwork/AddEditAdNetworkModal';
import { getAdNetworks } from '../services/adNetworkApi';
import { AdNetwork } from '../types/ad-network';
import { useProfile } from '../../(identity)/hooks/UserHooks';
import { Loader2 } from 'lucide-react';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<AdNetwork[]>([]);
    const [dataToEdit, setDataToEdit] = useState<AdNetwork | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState<boolean>(false);

    useEffect(() => {
        getAdNetworks().then((res) => {
            setData(res);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleUpdateData = (newData: AdNetwork) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: AdNetwork) => {
        setData([...data, newData]);
    };

    const toggleAddEditModal = () => {
        setIsAddEditModalOpen(!isAddEditModalOpen);
    };

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'shortOffset',
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Site',
            accessorKey: 'site',
            cell: (cell: any) => {
                return (
                    <>
                        {cell.getValue()}{' '}
                        <a href={'https://' + cell.row.original.site} target="_blank" rel="noreferrer">
                            <i className="ri-external-link-line"></i>
                        </a>
                    </>
                );
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Campanhas',
            accessorKey: 'campaigns.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Usuários',
            accessorKey: 'users.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'TAGs',
            accessorKey: 'adptags.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Criado em',
            accessorFn: (row: any) => {
                const date = new Date(row.createdAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Atualizado em',
            accessorFn: (row: any) => {
                const date = new Date(row.updatedAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                if (userProfile && userProfile.role === 'ROLE_SUPER_ADMIN') {
                    actionCell.push(
                        <TooltipProvider key="action_edit">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="btn text-primary d-inline-block"
                                        onClick={() => {
                                            setDataToEdit(row);
                                            toggleAddEditModal();
                                        }}
                                    >
                                        <i className="ri-pencil-fill fs-16"></i>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Editar</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }
                return <div className="flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'Ad Network | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="grid gap-4">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <CardTitle>Ad Networks</CardTitle>
                                    </div>
                                    <div>
                                        <Button
                                            onClick={() => {
                                                setDataToEdit(null);
                                                toggleAddEditModal();
                                            }}
                                            className="bg-green-600 hover:bg-green-700"
                                        >
                                            <i className="ri-add-line align-bottom mr-1"></i> Adicionar ad network
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {!loading ? (
                                    <TableContainer
                                        divClass="table-responsive"
                                        columns={(columns || [])}
                                        data={(data || [])}
                                        isGlobalFilter={true}
                                        customPageSize={30}
                                        SearchPlaceholder="Procurar..."
                                    />
                                ) : (
                                    <div className="flex justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                )}

                                <AddEditAdNetworkModal
                                    adNetwork={dataToEdit}
                                    isOpen={isAddEditModalOpen}
                                    toggle={toggleAddEditModal}
                                    onAddData={handleAddData}
                                    onUpdateData={handleUpdateData}
                                />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
